import * as React from 'react';
import { MicrophoneIcon } from '@heroicons/react/24/outline';
import { useVoiceWake } from '@/hooks/useVoiceWake';
import { useToast } from '@/hooks/useToast';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/utils';

/** One recognizer for the whole authenticated app. */
export function WakeWordController() {
  const enabled = useUIStore((state) => state.wakeWordEnabled);
  const sessionActivated = useUIStore((state) => state.wakeWordSessionActivated);
  const voiceCaptureBusy = useUIStore((state) => state.voiceCaptureBusy);
  const openVoiceCapture = useUIStore((state) => state.openVoiceCapture);
  const setRuntime = useUIStore((state) => state.setWakeWordRuntime);
  const { toast } = useToast();

  const handleWake = React.useCallback(() => {
    openVoiceCapture();
    toast({
      title: 'Listening — go ahead',
      description: 'Your voice note is recording now.',
      duration: 3200,
    });
  }, [openVoiceCapture, toast]);

  useVoiceWake({
    enabled: enabled && sessionActivated,
    suspended: voiceCaptureBusy,
    onWake: handleWake,
    onStatusChange: (status, listening, error) => {
      setRuntime({ status, listening, error });
    },
  });

  return null;
}

export function WakeWordIndicator() {
  const enabled = useUIStore((state) => state.wakeWordEnabled);
  const sessionActivated = useUIStore((state) => state.wakeWordSessionActivated);
  const listening = useUIStore((state) => state.wakeWordListening);
  const status = useUIStore((state) => state.wakeWordStatus);
  const error = useUIStore((state) => state.wakeWordError);
  const setEnabled = useUIStore((state) => state.setWakeWordEnabled);
  const activateSession = useUIStore((state) => state.activateWakeWordSession);

  if (!enabled) return null;
  const blocked = status === 'blocked' || status === 'error' || status === 'unsupported';
  const label = !sessionActivated
    ? 'Resume Hey Brain'
    : listening
      ? 'Hey Brain is listening'
      : status === 'paused'
        ? 'Hey Brain paused for recording'
        : blocked
          ? 'Hey Brain needs attention'
          : 'Starting Hey Brain';

  return (
    <button
      type="button"
      onClick={() => {
        if (!sessionActivated) activateSession();
        else setEnabled(false);
      }}
      className={cn(
        'group inline-flex min-w-0 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition focus:outline-none focus:ring-2 focus:ring-primary active:scale-[0.98]',
        listening && 'border-success/30 bg-success/10 text-success',
        !listening && !blocked && 'border-outline-variant/50 bg-surface-container-high text-text-secondary',
        blocked && 'border-error/30 bg-error/10 text-error',
      )}
      title={error || `${label}. Click to ${sessionActivated ? 'turn off' : 'resume'}.`}
      aria-label={`${label}. ${sessionActivated ? 'Turn off' : 'Resume'}`}
    >
      <span className="relative flex h-2 w-2 shrink-0">
        {listening ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-55 motion-reduce:animate-none" /> : null}
        <span className={cn('relative inline-flex h-2 w-2 rounded-full', listening ? 'bg-success' : blocked ? 'bg-error' : 'bg-text-muted')} />
      </span>
      <MicrophoneIcon className="h-4 w-4 shrink-0" />
      <span className="hidden max-w-36 truncate sm:inline">{label}</span>
    </button>
  );
}

export function WakeWordToggle() {
  const enabled = useUIStore((state) => state.wakeWordEnabled);
  const sessionActivated = useUIStore((state) => state.wakeWordSessionActivated);
  const listening = useUIStore((state) => state.wakeWordListening);
  const status = useUIStore((state) => state.wakeWordStatus);
  const setEnabled = useUIStore((state) => state.setWakeWordEnabled);
  const activateSession = useUIStore((state) => state.activateWakeWordSession);
  const unsupported = status === 'unsupported';

  return (
    <div className="border-t border-outline-variant/20 px-3 py-3">
      <button
        type="button"
        onClick={() => {
          if (enabled && !sessionActivated) activateSession();
          else setEnabled(!enabled);
        }}
        disabled={unsupported}
        className="flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-surface-container-high focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-55"
      >
        <span className={cn('mt-0.5 rounded-lg p-2', enabled ? 'bg-primary/10 text-primary' : 'bg-surface-container text-text-muted')}>
          <MicrophoneIcon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2 text-[13px] font-semibold text-text-primary">
            Hey Brain
            <span className={cn('text-[10px] font-medium', listening ? 'text-success' : 'text-text-muted')}>
              {listening ? 'Listening' : enabled && !sessionActivated ? 'Resume' : enabled ? 'Starting' : 'Off'}
            </span>
          </span>
          <span className="mt-1 block text-[11px] leading-4 text-text-muted">
            {enabled && !sessionActivated
              ? 'Click once to resume listening in this browser session.'
              : 'Listens across the app while this tab is open. It pauses when the app is minimized or your phone is locked.'}
          </span>
        </span>
      </button>
    </div>
  );
}
